import { forwardRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NotebookGrantResponse } from '@lp/shared';
import { ExternalLink } from 'lucide-react';
import { api, ApiError } from '../../lib/api.js';
import { Alert, Spinner } from '../ui.js';
import { HOSTED_APP_SANDBOX } from './HostedAppFrame.js';

/**
 * A runnable notebook: JupyterLite on the notebook's own origin
 * (`nb-<feature>.<apps host>`), with Python running in the learner's browser
 * through Pyodide. The server only serves files; nothing typed here reaches it.
 *
 * Like a hosted app, the frame gets same-origin access, popups and downloads —
 * its origin is the notebook's, not ours — because JupyterLite keeps the
 * learner's edits in that origin's storage and reads data files through a
 * service worker there.
 *
 * A notebook with environment variables opens through a grant: an address
 * with a pass, good for ten minutes, that the notebook origin trades for its
 * own cookie and then serves this viewer's kernel the variables. The grant is
 * refreshed in the background, and the frame follows the newest one until it
 * has loaded — it loads lazily, and a learner may open its tab long after the
 * page — then keeps that address, since a new one would reload it and lose
 * the kernel. "Open in a new tab" always carries a live one.
 */
const GRANT_REFRESH_MS = 8 * 60_000;

function useNotebookGrant(featureId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['notebook-grant', featureId],
    queryFn: () =>
      api.get<NotebookGrantResponse>(`/interactive/${encodeURIComponent(featureId)}/notebook/grant`),
    enabled,
    staleTime: GRANT_REFRESH_MS,
    refetchInterval: GRANT_REFRESH_MS,
    refetchOnWindowFocus: false,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
}

export const NotebookFrame = forwardRef<
  HTMLIFrameElement,
  {
    featureId: string;
    url: string;
    /** The author set environment variables, so the frame opens through a grant. */
    hasEnv: boolean;
    title: string;
    heightPx: number;
    onLoad?: () => void;
  }
>(function NotebookFrame({ featureId, url, hasEnv, title, heightPx, onLoad }, ref) {
  const grant = useNotebookGrant(featureId, hasEnv);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const height = { height: `${Math.min(Math.max(heightPx, 160), 2000)}px` };

  // The plain address, the newest grant, or — if no grant can be had — the
  // plain address without the variables.
  const ready = !hasEnv || grant.isSuccess || grant.isError;
  const latest = hasEnv && grant.data ? grant.data.url : url;
  const frameUrl = loadedUrl ?? latest;

  if (!ready) {
    return (
      <div
        style={height}
        className="flex w-full items-center justify-center gap-3 rounded-control border border-line bg-surface-2 text-sm text-ink-muted"
      >
        <Spinner label="Opening the notebook" />
        Opening the notebook…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {hasEnv && grant.isError && !grant.data ? (
        <Alert tone="warn" title="The notebook’s keys could not be loaded">
          It opens without them, so cells that call an outside service may fail. Reload the page
          to try again.
        </Alert>
      ) : null}
      <iframe
        ref={ref}
        src={frameUrl}
        title={title || 'Notebook'}
        sandbox={HOSTED_APP_SANDBOX}
        allow="clipboard-read; clipboard-write; fullscreen"
        referrerPolicy="no-referrer"
        loading="lazy"
        onLoad={() => {
          setLoadedUrl((current) => current ?? frameUrl);
          onLoad?.();
        }}
        style={height}
        className="w-full rounded-control border border-line bg-surface-2"
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <a
          href={latest}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-brand-text hover:underline"
        >
          <ExternalLink size={13} aria-hidden />
          Open in a new tab
        </a>
        <p className="text-xs text-ink-muted">
          Python runs in your browser — the first run takes a few seconds to load. Your changes
          are kept in this browser only; use File → Download to keep a copy.
        </p>
      </div>
    </div>
  );
});
